import { ObjectMeta } from "../../common";
import { z } from "zod";
//
// Token types
//
export const DesignTokenType = z.enum([
    "Color",
    "Border",
    "Gradient",
    "Shadow",
    "Dimension",
    "Duration",
    "Size",
    "Space",
    "Opacity",
    "FontSize",
    "LineHeight",
    "LetterSpacing",
    "ParagraphSpacing",
    "BorderWidth",
    "BorderRadius",
    "ZIndex",
    "Image",
    "String",
    "ProductCopy",
    "FontFamily",
    "FontWeight",
    "TextDecoration",
    "TextCase",
    "Visibility",
    "Typography",
    "Blur",
    "Font",
]);
export const tokenElementTypes = [
    ...DesignTokenType.options.filter((v) => v !== "Font"),
];
//
// Non-token types
//
export const DesignElementType = DesignTokenType.or(z.enum([
    "Component",
    "Theme",
    "Documentation",
    "DocumentationPage",
    "DesignSystemComponent",
    "ElementGroup",
    "FigmaNodeStructure",
    "FigmaNodeReference",
    "PageBlock",
]));
export function isTokenType(type) {
    return DesignTokenType.safeParse(type).success;
}
const numberTokenTypes = new Set([
    "Dimension",
    "Duration",
    "Size",
    "Space",
    "Opacity",
    "FontSize",
    "LineHeight",
    "LetterSpacing",
    "ParagraphSpacing",
    "BorderWidth",
    "BorderRadius",
    "ZIndex",
]);
const stringTokenTypes = new Set([
    "String",
    "ProductCopy",
    "FontFamily",
    "FontWeight",
]);
const fallbackNumberValue = 14;
export function areTokenTypesCompatible(lhs, rhs, isNonCompatibleTypeChangesEnabled = false) {
    if (lhs === rhs)
        return true;
    if (numberTokenTypes.has(lhs) && numberTokenTypes.has(rhs))
        return true;
    if (stringTokenTypes.has(lhs) && stringTokenTypes.has(rhs))
        return true;
    if (isNonCompatibleTypeChangesEnabled &&
        stringTokenTypes.has(lhs) &&
        numberTokenTypes.has(rhs))
        return true;
    return false;
}
export function castStringToDimensionValue(lhs, rhs, value) {
    var _a;
    if (stringTokenTypes.has(lhs) && numberTokenTypes.has(rhs) && value) {
        const newValue = Number.parseFloat((_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : "");
        const measure = Number.isNaN(newValue) ? fallbackNumberValue : newValue;
        return { unit: "Pixels", measure };
    }
    return value;
}
//
// Element category
//
export const DesignElementCategory = z.enum([
    "Token",
    "Component",
    "DesignSystemComponent",
    "DocumentationPage",
    "Theme",
    "PageBlock",
]);
// Elements
export const DesignSystemElementExportProps = z.object({
    isAsset: z
        .boolean()
        .nullish()
        .transform((v) => v !== null && v !== void 0 ? v : false),
    codeName: z.string().nullish(),
});
export const ShallowDesignElement = z.object({
    id: z.string(),
    persistentId: z.string(),
    designSystemVersionId: z.string(),
    type: DesignElementType,
    brandPersistentId: z.string().optional(),
    collectionPersistentId: z.string().optional(),
    collectionPersistentIds: z.string().array(),
    parentPersistentId: z.string().optional(),
    shortPersistentId: z.string().optional(),
    childType: DesignElementType.optional(),
    sortOrder: z.number(),
    origin: z.record(z.any()).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const DesignElement = ShallowDesignElement.extend({
    meta: ObjectMeta,
    slug: z.string().optional(),
    userSlug: z.string().optional(),
    exportProperties: DesignSystemElementExportProps.optional(),
    data: z.record(z.any()),
    origin: z.record(z.any()).optional(),
});
export const HierarchicalElements = DesignTokenType.or(z.enum(["Component", "DesignSystemComponent", "DocumentationPage"]));
